import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  getFlaggedQuestions,
  getFlaggedQuestionStats,
  reviewQuestion,
  dismissFlag,
  previewQuestionImpact,
  formatUserName,
  formatScoreChange,
  getScoreChangeColor
} from '../services/questionReviewService';
import {
  FlaggedQuestionDetails,
  FlaggedQuestionStats,
  QuestionImpactPreview,
  ReviewQuestionRequest,
  DismissFlagRequest
} from '../types';
import {
  Card,
  CardBody,
  Section,
  Button,
  Badge,
  Grid
} from '../components/BaseComponents';

/**
 * AdminQuestionReview - Administrative interface for reviewing flagged questions
 *
 * Features:
 * - List of all flagged questions with details
 * - Review and update question answers
 * - Preview impact of answer changes on user scores
 * - Dismiss flags without changes
 * - Statistics dashboard
 */
export const AdminQuestionReview: React.FC = () => {
  const { currentUser } = useUser();

  // State management
  const [flaggedQuestions, setFlaggedQuestions] = useState<FlaggedQuestionDetails[]>([]);
  const [stats, setStats] = useState<FlaggedQuestionStats>({ totalFlagged: 0, pendingReview: 0, reviewed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showPendingOnly, setShowPendingOnly] = useState(true);

  // Review state
  const [reviewingQuestionId, setReviewingQuestionId] = useState<number | null>(null);
  const [newAnswerKey, setNewAnswerKey] = useState('');
  const [title, setTitle] = useState('');
  const [questionDescription, setQuestionDescription] = useState('');
  const [workedSolution, setWorkedSolution] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [impactPreview, setImpactPreview] = useState<QuestionImpactPreview | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      loadData();
    }
  }, [currentUser, showPendingOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [questionsData, statsData] = await Promise.all([
        getFlaggedQuestions(showPendingOnly),
        getFlaggedQuestionStats(),
      ]);

      setFlaggedQuestions(questionsData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load flagged questions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartReview = (question: FlaggedQuestionDetails) => {
    setReviewingQuestionId(question.questionId);
    setNewAnswerKey(question.currentAnswerKey);
    setTitle(question.title);
    setQuestionDescription(question.questionDescription);
    setWorkedSolution(question.workedSolution || '');
    setReviewNotes('');
    setImpactPreview(null);
  };

  const handleCancelReview = () => {
    setReviewingQuestionId(null);
    setNewAnswerKey('');
    setReviewNotes('');
    setImpactPreview(null);
  };

  const handlePreviewImpact = async (questionId: number) => {
    if (!newAnswerKey.trim()) return;

    try {
      const preview = await previewQuestionImpact(questionId, newAnswerKey.trim());
      setImpactPreview(preview);
    } catch (err: any) {
      setError(`Failed to preview impact: ${err.message}`);
    }
  };

  const handleSubmitReview = async (questionId: number) => {
    if (!newAnswerKey.trim()) return;

    setIsSubmitting(true);
    try {
      const request: ReviewQuestionRequest = {
        newAnswerKey: newAnswerKey.trim(),
        title: title.trim(),
        questionDescription: questionDescription.trim(),
        workedSolution: workedSolution.trim() || undefined,
        reviewNotes: reviewNotes.trim() || undefined,
      };

      await reviewQuestion(questionId, request);

      setSuccessMessage('Question reviewed successfully.');
      setTimeout(() => setSuccessMessage(null), 5000);

      handleCancelReview();
      await loadData(); // Reload the data
    } catch (err: any) {
      setError(`Failed to review question: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismissFlag = async (questionId: number) => {
    setIsSubmitting(true);
    try {
      const request: DismissFlagRequest = {
        reviewNotes: reviewNotes.trim() || 'Flag dismissed - no changes needed',
      };

      await dismissFlag(questionId, request);

      setSuccessMessage('Question flag dismissed successfully.');
      setTimeout(() => setSuccessMessage(null), 5000);

      handleCancelReview();
      await loadData(); // Reload the data
    } catch (err: any) {
      setError(`Failed to dismiss flag: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if user has permission
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <Section>
            <Card className="border-red-200 bg-red-50">
              <CardBody className="text-center py-12">
                <h3 className="text-lg font-medium text-red-900 mb-2">Access Denied</h3>
                <p className="text-red-700">
                  You need admin permissions to access the question review system.
                </p>
              </CardBody>
            </Card>
          </Section>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading flagged questions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <Section className="section-spacing">
          <Card className="card-hero" noHover={true}>
            <CardBody>
              <h1 className="text-page-title">Question Review Dashboard</h1>
              <p className="text-indigo-100">
                Review and manage questions flagged by students for accuracy issues
              </p>
            </CardBody>
          </Card>
        </Section>

        {/* Success Message */}
        {successMessage && (
          <Section className="section-spacing">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            </div>
          </Section>
        )}

        {/* Error Message */}
        {error && (
          <Section className="section-spacing">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-red-700">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setError(null)}
                  className="text-red-600 border-red-300"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </Section>
        )}

        {/* Statistics */}
        <Section className="section-spacing">
          <Card>
            <CardBody>
              <h2 className="text-card-title mb-6">Review Statistics</h2>
              <Grid cols={3} gap="lg">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">{stats.pendingReview}</div>
                  <div className="text-sm text-gray-600">Pending Review</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{stats.reviewed}</div>
                  <div className="text-sm text-gray-600">Reviewed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-600">{stats.totalFlagged}</div>
                  <div className="text-sm text-gray-600">Total Flagged</div>
                </div>
              </Grid>
            </CardBody>
          </Card>
        </Section>

        {/* Filters */}
        <Section className="section-spacing">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <h2 className="text-card-title">Flagged Questions</h2>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showPendingOnly}
                      onChange={(e) => setShowPendingOnly(e.target.checked)}
                      className="mr-2 rounded"
                    />
                    <span className="text-sm text-gray-700">Show pending only</span>
                  </label>
                  <Button variant="outline" size="sm" onClick={loadData}>
                    Refresh
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Section>

        {/* Questions List */}
        <Section>
          {flaggedQuestions.length === 0 ? (
            <Card>
              <CardBody className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Flagged Questions</h3>
                <p className="text-gray-600">
                  {showPendingOnly
                    ? 'There are no questions pending review at this time.'
                    : 'No questions have been flagged for review yet.'
                  }
                </p>
              </CardBody>
            </Card>
          ) : (
            <div className="space-y-6">
              {flaggedQuestions.map((question) => (
                <Card key={question.questionId}>
                  <CardBody>
                    {/* Question Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Badge className="bg-gray-100 text-gray-800 text-xs">
                          Question #{question.questionId}
                        </Badge>
                        <Badge className={getDifficultyColor(question.difficulty)}>
                          {question.difficulty}
                        </Badge>
                        {question.reviewedAt && (
                          <Badge className="bg-green-100 text-green-800">
                            Reviewed
                          </Badge>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <div>Flagged: {formatDate(question.flaggedAt)}</div>
                        <div>By: {formatUserName(question.flaggedBy)}</div>
                      </div>
                    </div>

                    {/* Question Content */}
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-900 mb-2">{question.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{question.questionDescription}</p>
                    </div>

                    {/* Flag Reason */}
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                      <h4 className="font-medium text-orange-900 mb-1">Flag Reason:</h4>
                      <p className="text-orange-800 text-sm">{question.flagReason}</p>
                    </div>

                    {/* Current Answer */}
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Current Answer:</h4>
                      <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                        {question.currentAnswerKey}
                      </div>
                    </div>

                    {/* Review Section */}
                    {reviewingQuestionId === question.questionId ? (
                      <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <h4 className="font-medium text-gray-900 mb-4">Review Question</h4>

                        {[
                          { id: 'review-title', label: 'Title', value: title, setValue: setTitle },
                          { id: 'review-description', label: 'Question wording', value: questionDescription, setValue: setQuestionDescription },
                          { id: 'review-solution', label: 'Worked solution', value: workedSolution, setValue: setWorkedSolution },
                        ].map(field => (
                          <div className="mb-4" key={field.id}>
                            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-2">{field.label}:</label>
                            <textarea id={field.id} value={field.value} onChange={e => field.setValue(e.target.value)}
                              rows={field.id === 'review-title' ? 1 : 4} disabled={isSubmitting}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                          </div>
                        ))}

                        {/* New Answer Input */}
                        <div className="mb-4">
                          <label htmlFor="new-answer" className="block text-sm font-medium text-gray-700 mb-2">
                            New Answer Key:
                          </label>
                          <input
                            id="new-answer"
                            type="text"
                            value={newAnswerKey}
                            onChange={(e) => setNewAnswerKey(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Enter the correct answer"
                            disabled={isSubmitting}
                          />
                        </div>

                        {/* Review Notes */}
                        <div className="mb-4">
                          <label htmlFor="review-notes" className="block text-sm font-medium text-gray-700 mb-2">
                            Review Notes (optional):
                          </label>
                          <textarea
                            id="review-notes"
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                            rows={3}
                            placeholder="Add notes about the review..."
                            disabled={isSubmitting}
                          />
                        </div>

                        {/* Impact Preview */}
                        {(newAnswerKey.trim() !== question.currentAnswerKey || title !== question.title || questionDescription !== question.questionDescription || workedSolution !== (question.workedSolution || '')) && (
                          <div className="mb-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreviewImpact(question.questionId)}
                              disabled={!newAnswerKey.trim() || isSubmitting}
                            >
                              Preview Impact
                            </Button>
                          </div>
                        )}

                        {impactPreview && (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                            <h5 className="font-medium text-blue-900 mb-2">Impact Preview:</h5>
                            <div className="text-sm text-blue-800">
                              <p>• {impactPreview.impactStats.totalAttempts} total quiz attempts affected</p>
                              <p>• {impactPreview.impactStats.uniqueUsers} unique users affected</p>
                              <p>• {impactPreview.preview.totalAffectedAttempts} attempts will have score changes</p>
                              {impactPreview.preview.potentialScoreChanges.length > 0 && (
                                <div className="mt-2">
                                  <p className="font-medium">Score changes:</p>
                                  <div className="max-h-24 overflow-y-auto">
                                    {impactPreview.preview.potentialScoreChanges.slice(0, 5).map((change, index) => (
                                      <div key={index} className="text-xs">
                                        {change.userEmail}: {change.currentScore}% → {change.newScore}%
                                        <span className={getScoreChangeColor(change.scoreChange)}>
                                          ({formatScoreChange(change.scoreChange)})
                                        </span>
                                      </div>
                                    ))}
                                    {impactPreview.preview.potentialScoreChanges.length > 5 && (
                                      <div className="text-xs text-blue-600">
                                        ... and {impactPreview.preview.potentialScoreChanges.length - 5} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3">
                          <Button
                            variant="secondary"
                            onClick={handleCancelReview}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDismissFlag(question.questionId)}
                            disabled={isSubmitting}
                            className="text-gray-600 border-gray-300"
                          >
                            {isSubmitting ? 'Processing...' : 'Dismiss Flag'}
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => handleSubmitReview(question.questionId)}
                            disabled={!newAnswerKey.trim() || isSubmitting}
                          >
                            {isSubmitting ? 'Processing...' : 'Submit Review'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 flex justify-end">
                        {!question.reviewedAt ? (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleStartReview(question)}
                          >
                            Review Question
                          </Button>
                        ) : (
                          <div className="text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              <span>Reviewed by {formatUserName(question.reviewedBy!)}</span>
                              <span>•</span>
                              <span>{formatDate(question.reviewedAt)}</span>
                            </div>
                            {question.reviewNotes && (
                              <div className="mt-1 text-xs italic">"{question.reviewNotes}"</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default AdminQuestionReview;